import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, RefreshCw, ArrowLeft, CheckCircle2, Clock, XCircle, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminEmailsPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [retryingId, setRetryingId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/email-queue/stats`, { headers }),
        axios.get(`${API}/admin/email-queue/list?status=${statusFilter}&type=${typeFilter}&limit=100`, { headers })
      ]);
      setStats(statsRes.data);
      setItems(listRes.data.items || []);
    } catch (e) {
      // Surface failure silently in UI; log for dev
      // eslint-disable-next-line no-console
      console.error("Failed to load email queue:", e);
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeader, statusFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRetry = async (emailId) => {
    if (!window.confirm("Retry this failed email?")) return;
    setRetryingId(emailId);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/email-queue/retry/${emailId}`, {}, { headers });
      await fetchData();
    } catch (e) {
      alert("Retry failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRetryingId(null);
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      sent: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10", label: "Sent" },
      queued: { icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10", label: "Queued" },
      failed: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10", label: "Failed" }
    }[status] || { icon: Clock, color: "text-slate-400", bg: "bg-slate-700", label: status };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${cfg.color} ${cfg.bg}`}>
        <Icon size={12} /> {cfg.label}
      </span>
    );
  };

  const formatTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-slate-900 p-4 pb-24" data-testid="admin-emails-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white"
            data-testid="admin-emails-back"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Mail size={24} className="text-emerald-400" /> Email Queue
            </h1>
            <p className="text-slate-400 text-sm">Monitor and manage outgoing emails</p>
          </div>
          <button
            onClick={fetchData}
            className="ml-auto text-slate-400 hover:text-white"
            disabled={loading}
            data-testid="admin-emails-refresh"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {stats && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><div className="text-slate-400 text-xs uppercase">Sent today</div><div className="text-2xl font-bold text-emerald-400" data-testid="stat-sent-today">{stats.today_sent}</div></div>
              <div><div className="text-slate-400 text-xs uppercase">Budget left</div><div className="text-2xl font-bold text-white">{stats.today_budget_remaining}</div></div>
              <div><div className="text-slate-400 text-xs uppercase">Queued now</div><div className="text-2xl font-bold text-amber-400">{stats.queued_for_today}</div></div>
              <div><div className="text-slate-400 text-xs uppercase">Scheduled</div><div className="text-2xl font-bold text-slate-300">{stats.queued_for_later}</div></div>
            </div>
            {stats.failed_last_24h > 0 && (
              <div className="text-red-400 text-sm">{stats.failed_last_24h} email(s) failed in last 24h</div>
            )}
            <div className="pt-3 border-t border-slate-700 text-sm text-slate-400 space-y-1">
              <div>Transfer Window: {stats.by_type.transfer_window.sent} sent, {stats.by_type.transfer_window.queued} queued, {stats.by_type.transfer_window.failed} failed</div>
              <div>Welcome: {stats.by_type.welcome.sent} sent, {stats.by_type.welcome.queued} queued, {stats.by_type.welcome.failed} failed</div>
            </div>
          </div>
        )}

        <div className="flex gap-2 items-center">
          <Filter size={16} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="status-filter"
          >
            <option value="all">All statuses</option>
            <option value="queued">Queued</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="type-filter"
          >
            <option value="all">All types</option>
            <option value="transfer_window">Transfer Window</option>
            <option value="welcome">Welcome</option>
          </select>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          {items.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No emails match filters</div>
          ) : (
            <div className="divide-y divide-slate-700">
              {items.map(item => (
                <div key={item.id} className="p-4 flex items-center gap-4 hover:bg-slate-800/80" data-testid={`email-row-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {statusBadge(item.status)}
                      <span className="text-xs text-slate-500">{item.type}</span>
                      {item.week_number ? <span className="text-xs text-slate-500">· Week {item.week_number}</span> : null}
                    </div>
                    <div className="text-white font-medium truncate">{item.display_name || item.email}</div>
                    <div className="text-slate-400 text-xs truncate">{item.email}</div>
                    <div className="text-slate-500 text-xs mt-1">{item.subject}</div>
                    {item.error && <div className="text-red-400 text-xs mt-1">{item.error}</div>}
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>{item.status === "sent" ? `Sent ${formatTime(item.sent_at)}` : `Scheduled ${formatTime(item.scheduled_for)}`}</div>
                    {item.retry_count > 0 && <div className="text-amber-400 mt-1">Retries: {item.retry_count}</div>}
                  </div>
                  {item.status === "failed" && (
                    <button
                      onClick={() => handleRetry(item.id)}
                      disabled={retryingId === item.id}
                      className="text-xs bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 text-white px-3 py-1.5 rounded-lg"
                      data-testid={`retry-btn-${item.id}`}
                    >
                      {retryingId === item.id ? "..." : "Retry"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
